import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

const Container = styled.div`
  ${tw`
    max-w-screen-xl
    mx-auto
    px-4
    md:px-8
    py-12
    bg-white
    min-h-screen
  `};
`;

const MarkdownWrapper = styled.div`
  ${tw`
    prose
    prose-slate
    max-w-none
  `};

  table {
    ${tw`w-full border-collapse border border-gray-200 mt-4 mb-8`};
  }

  th {
    ${tw`bg-gray-100 border border-gray-200 p-3 text-left font-bold text-sm text-gray-700`};
  }

  td {
    ${tw`border border-gray-200 p-3 text-sm text-gray-600`};
  }

  h1 {
    ${tw`text-3xl font-extrabold text-gray-900 border-b-4 border-blue-500 pb-2 mb-8`};
  }

  h2 {
    ${tw`text-2xl font-bold text-gray-800 mt-12 mb-6`};
  }

  code {
    ${tw`bg-gray-100 text-red-600 px-1.5 py-0.5 rounded font-mono text-sm`};
  }

  pre {
    ${tw`bg-gray-900 text-gray-100 p-4 rounded-xl overflow-x-auto my-6`};
    code {
      ${tw`bg-transparent text-gray-100 p-0 text-xs text-white`};
    }
  }
`;

const ApiUsageAnalysisPage: React.FC = () => {
  const [content, setContent] = useState<string>('');

  useEffect(() => {
    const markdownLines = [
      '# Autopilotee API Usage Analysis (Web App)',
      '',
      'This document provides a summary of the GraphQL APIs (Resolvers) from the backend that are currently used or unused within the `autopilotee-cars` (Web Frontend) codebase.',
      '',
      '---',
      '',
      '## 1. Used APIs & Associated Pages',
      '',
      'These APIs are actively integrated and called from various frontend containers and components.',
      '',
      '| API / Resolver Method | Type | Frontend Usage Location | Purpose | Created At | Updated At |',
      '| :--- | :--- | :--- | :--- | :--- | :--- |',
      '| `calculateCarPricingWithoutTaxResolver` | Query | `ViewCarPage`, `CheckoutPage` | Calculates estimated trip costs, taxes, and discounts based on selected dates and car details. | 2024-12-01 | 2026-01-03 |',
      '| `checkCarAvailability` | Mutation | `CheckoutPage`, `SearchResultPage` | Validates if a vehicle is available for the requested time range before proceeding. | 2024-12-26 | 2026-01-03 |',
      '| `createBookingIntent` | Mutation | `CheckoutPage` | Initializes a booking record in `PENDING` status to "soft-lock" the car. | 2024-12-26 | 2026-01-05 |',
      '| `booking` | Query | `BookingDetails`, `ChatDetailPage`, `CheckoutPage` | Fetches full details for a specific booking (status, receipt, car info). | 2024-12-26 | 2026-01-05 |',
      '| `myBookings` | Query | `MyBookingsPage` | Lists all bookings where the current user is the Guest. | 2024-12-26 | 2026-01-05 |',
      '| `bookingsForMyListings` | Query | `BookedMyCarsPage` | Lists all bookings for cars owned by the current user (Host view). | 2024-12-26 | 2026-01-05 |',
      '| `carBookings` | Query | `BookingsByCar` | Fetches all bookings associated with a single specific vehicle. | 2024-12-26 | 2026-01-05 |',
      '| `confirmBooking` | Mutation | `CheckoutPage` | Finalizes the booking status to `BOOKED` after successful payment confirmation. | 2024-12-26 | 2026-01-05 |',
      '| `verifyBookingPayment` | Mutation | `CheckoutPage`, `BookingDetails` | Backend polling/verification to ensure the payment status matches the Stripe state. | 2025-10-15 | 2026-01-05 |',
      '| `cancelBooking` | Mutation | `CheckoutPage`, `BookingDetails` | Terminating a booking intent or cancelling an active trip (handles refund logic). | 2024-12-26 | 2026-01-05 |',
      '| `updateBooking` | Mutation | `BookingDetails` | General updates to booking metadata. | 2024-12-26 | 2026-01-05 |',
      '| `updateBookingProtectionPlan` | Mutation | `CheckoutPage`, `BookingDetails` | Allows guests to change their protection plan. | 2025-05-12 | 2026-01-05 |',
      '| `calculateTripExtension` | Mutation | `BookingDetails` | Calculates cost difference for extending or shortening an existing trip. | 2025-08-20 | 2026-01-05 |',
      "| `addDriverToBooking` | Mutation | `BookingDetails` | Registers an additional driver's email and verification status. | 2025-07-15 | 2026-01-05 |",
      '| `holdSecurityDeposit` | Mutation | `BookingDetails` | Manually triggers the capture of the authorized security deposit. | 2025-11-22 | 2026-01-05 |',
      '| `releaseSecurityDeposit` | Mutation | `BookingDetails` | Releases the held security deposit (full or partial) back to the guest. | 2025-11-22 | 2026-01-05 |',
      '| `removeBookingPenalties` | Mutation | `BookingDetails` | Admin/Host tool to waive cancellation or modification fees for a guest. | 2025-11-30 | 2026-01-05 |',
      '| `updateBookingMinimumRentalDays`| Mutation | `BookingDetails` | Adjusts the minimum rental constraint for a specific booking. | 2025-12-16 | 2026-01-05 |',
      '| `requestCarSwap` | Mutation | `CarSwapModal` | Initiates a request to change the assigned vehicle for a trip. | 2025-12-29 | 2026-01-03 |',
      '| `approveCarSwap` | Mutation | `BookingDetails` | Guest approval of a proposed vehicle swap. | 2025-12-29 | 2026-01-03 |',
      '| `rejectCarSwap` | Mutation | `BookingDetails` | Guest rejection of a proposed vehicle swap. | 2025-12-29 | 2026-01-03 |',
      '| `forceApproveCarSwap` | Mutation | `BookingDetails` | Administrative override to finalize a car swap without waiting for guest approval. | 2025-12-29 | 2026-01-03 |',
      '| `forceDeclineCarSwap` | Mutation | `BookingDetails` | Administrative override to cancel a pending swap request. | 2025-12-29 | 2026-01-03 |',
      '| `createSetupIntentForBooking` | Mutation | `CheckoutPage` (Deposit First) | Creates a Stripe SetupIntent to authorize the security deposit. | 2024-12-31 | 2026-01-05 |',
      '| `createRentalPaymentIntent` | Mutation | `CheckoutPage` (Deposit First) | Creates an off-session PaymentIntent for the rental portion. | 2024-12-31 | 2026-01-05 |',
      '| `createPaymentIntent` | Mutation | None (old app versions only) | Pre-SetupIntent checkout. NEW_BOOKING only; the server charges the booking total (spec 013 P6). | 2024-12-31 | 2026-10-09 |',
      '',
      '---',
      '',
      '## 2. Unused / Undiscovered APIs',
      '',
      'These APIs are defined in the backend but do not appear to be called from the `autopilotee-cars` web application. They may be used in the Mobile App (RN) or reserved for future features.',
      '',
      '| API / Resolver Method | Type | Intended Purpose | Created At | Updated At |',
      '| :--- | :--- | :--- | :--- | :--- |',
      '| `bookings` | Query | Admin-level query to list ALL bookings in the system. | 2024-12-26 | 2026-01-05 |',
      '| `cancelledBookingsAsHost` | Query | Filtered view for hosts to see only their cancelled trip history. | 2024-12-26 | 2026-01-05 |',
      '| `cancelledBookingsAsGuest` | Query | Filtered view for guests to see only their cancelled trip history. | 2024-12-26 | 2026-01-05 |',
      '| `confirmTripExtension` | Mutation | Actually charging the user for an extension (often handled via general payment flows). | 2025-08-20 | 2026-01-05 |',
      '| `reportCarDamage` | Mutation | Reporting vehicle damage to trigger a claim against the security deposit. | 2025-11-22 | 2026-01-05 |',
      '| `resolveDepositDispute` | Mutation | Admin utility to settle disputes regarding deposit claims. | 2025-11-22 | 2026-01-05 |',
      '| `uploadTripPhoto` | Mutation | Uploading check-in/check-out verification photos. | 2025-06-10 | 2026-01-05 |',
      '| `confirmCarConditionClear` | Mutation | Host confirming car returned in good condition to release deposit. | 2025-11-22 | 2026-01-05 |',
      '| `removeDriverFromBooking` | Mutation | Removing a previously authorized additional driver. | 2025-07-15 | 2026-01-05 |',
      '| `chargePostTripFee` | Mutation | Charging for post-trip incidents (cleaning, smoking, late return). | 2025-03-05 | 2026-01-05 |',
      '| `executeRefund` | Mutation | Triggering an actual money refund via Stripe for cancelled bookings. | 2024-12-31 | 2026-01-05 |',
      '| `createDepositPaymentIntent` | Mutation | Direct capture of a deposit (superseded by SetupIntent flow). | 2024-12-31 | 2026-01-05 |',
      '| `getPaymentMethodFromSetupIntent` | Query | Retrieving specific card details from a completed SetupIntent. | 2024-12-31 | 2026-01-05 |',
      '',
      '---',
      '',
      '## 3. Analysis Findings',
      '',
      '- **Core Lifecycle Integrated**: The entire flow from search (`calculatePricing`) to booking (`createIntent`) and payment (`SetupIntent/RentalIntent`) is fully integrated into the web frontend.',
      '- **Post-Booking Operations**: Most operational tasks (Swapping, Deposit Management, Penalty Waiving) are concentrated in the \`BookingDetails\` container.',
      '- **Mobile-Heavy Features**: Features like **Damage Reporting** and **Check-in Photos** are currently absent from the web app, likely because these are "on-site" actions primarily handled via the Mobile (React Native) app.',
      '- **Admin Features**: Wide-scope queries (e.g., `bookings`) and specialized dispute resolution mutations are expectedly unused in the consumer-facing web application.',
      '',
      '---',
      '*Generated by Antigravity API Analyzer*',
    ];
    setContent(markdownLines.join('\n'));
  }, []);

  return (
    <Container>
      <MarkdownWrapper>
        <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
          {content}
        </ReactMarkdown>
      </MarkdownWrapper>
    </Container>
  );
};

export default ApiUsageAnalysisPage;
